import React, { useEffect, useState, useCallback } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import AdminLayout from '../../components/layout/AdminLayout';
import { getPosSalesDashboard, getPosSalesByProduct } from '../../api/posSales';
import './posSales.css';

// 차트 색상 - 디자인 토큰(현대카드 톤)과 맞춤. onetak=the Red, POS=블랙.
const C_ONETAK = '#da0011';
const C_POS = '#111111';

const num = (v) => Number(v || 0).toLocaleString();
const won = (v) => `${num(v)}원`;
// 날짜는 반드시 로컬(브라우저=KST) 기준으로 만든다. toISOString()은 UTC라
// 한국시간 오전에는 하루 전 날짜가 되어 '오늘' 매출이 조회되지 않는다.
const pad2 = (n) => String(n).padStart(2, '0');
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; };
const monthStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`; };
// 축 눈금 표기 - 만 단위로 반올림하면 촘촘한 눈금이 "1만/1만"처럼 중복되므로,
// 1만 미만은 원 단위, 그 이상은 만 단위(필요 시 소수 1자리)로 손실 없이 표기한다.
const manFmt = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) < 10000) return n.toLocaleString();
  const man = n / 10000;
  const s = Number.isInteger(man) ? man.toLocaleString() : man.toFixed(1);
  return `${s}만`;
};

// 도넛 차트 데이터: 상위 6개 상품 + 나머지는 '기타'로 합산.
function buildPieData(products) {
  const top = products.slice(0, 6).map((p) => ({ id: p.productName, label: p.productName, value: Number(p.amount) || 0 }));
  const restAmt = products.slice(6).reduce((s, p) => s + (Number(p.amount) || 0), 0);
  if (restAmt > 0) top.push({ id: '기타', label: '기타', value: restAmt });
  return top;
}

const chartTooltip = (label, value) => (
  <div style={{ padding: '5px 9px', background: '#fff', border: '1px solid #e5e5e5', borderRadius: 3, fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
    <strong>{label}</strong><br />{Number(value).toLocaleString()}원
  </div>
);

/**
 * POS 매출 대시보드 - 매장별 일/월 매출을 onetak 자체매출(결제완료)과 POS 실매출(전송완료)로
 * 비교한다. 매장 행을 클릭하면 상품별 매출 통계(막대/도넛 차트)가 열린다.
 */
export default function PosSalesDashboardPage() {
  const [period, setPeriod] = useState('DAY');
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedStore, setSelectedStore] = useState(null); // { storeId, storeName }
  const [products, setProducts] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [productError, setProductError] = useState('');

  const fetchData = useCallback(async (p, d) => {
    setLoading(true);
    setError('');
    try {
      const res = await getPosSalesDashboard(p, d);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'POS 매출을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(period, date); }, [period, date, fetchData]);

  const fetchProducts = useCallback(async (storeId, p, d) => {
    setProductLoading(true);
    setProductError('');
    try {
      const res = await getPosSalesByProduct(storeId, p, d);
      setProducts(res.data || []);
    } catch (err) {
      setProductError(err.response?.data?.message || '상품별 매출을 불러오지 못했습니다.');
      setProducts([]);
    } finally {
      setProductLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedStore) fetchProducts(selectedStore.storeId, period, date);
  }, [period, date, selectedStore, fetchProducts]);

  const switchPeriod = (p) => {
    setPeriod(p);
    setDate(p === 'MONTH' ? monthStr() : todayStr());
  };

  const rows = data?.rows || [];
  const totalOnetak = data?.totalOnetakAmount || 0;
  const totalPos = data?.totalPosAmount || 0;
  const diff = totalOnetak - totalPos;

  // 매장 비교 차트(onetak vs POS, 상위 8개 매장)
  const storeChartData = rows.slice(0, 8).map((r) => ({
    store: r.storeName,
    onetak: Number(r.onetakAmount) || 0,
    POS: Number(r.posAmount) || 0,
  }));

  // 도넛 차트 데이터 + 합계(조각 안 퍼센트 표시에 사용)
  const pieData = buildPieData(products);
  const pieTotal = pieData.reduce((s, d) => s + d.value, 0) || 1;
  const clip = (s, n = 8) => (s && s.length > n ? `${s.slice(0, n)}…` : s);

  return (
    <AdminLayout title="POS매출" menuUrl="/admin/pos-sales">
      <div className="pos-page">
        <div className="page-header">
          <div>
            <h1>POS 매출 대시보드</h1>
            <div className="subtitle">매장별 일·월 매출을 onetak 결제매출과 POS 전송매출로 비교합니다. 매장을 클릭하면 상품별 통계를 봅니다.</div>
          </div>
        </div>

        <div className="card pos-card pos-toolbar">
          <div className="btn-group">
            <button type="button" className={`btn ${period === 'DAY' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => switchPeriod('DAY')}>일 매출</button>
            <button type="button" className={`btn ${period === 'MONTH' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => switchPeriod('MONTH')}>월 매출</button>
          </div>
          <input
            className="input"
            type={period === 'MONTH' ? 'month' : 'date'}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>

        <div className="pos-kpis">
          <div className="pos-kpi onetak">
            <div className="label">onetak 결제매출</div>
            <div className="value">{num(totalOnetak)}<span className="unit">원</span></div>
          </div>
          <div className="pos-kpi pos">
            <div className="label">POS 전송매출</div>
            <div className="value">{num(totalPos)}<span className="unit">원</span></div>
          </div>
          <div className="pos-kpi gap">
            <div className="label">미전송 차액 (onetak − POS)</div>
            <div className={`value ${diff > 0 ? 'pos-neg' : 'pos-ok'}`}>{num(diff)}<span className="unit">원</span></div>
            <div className="sub">차액이 클수록 POS에 미반영된 매출이 많습니다.</div>
          </div>
        </div>

        <div className="card pos-card">
          <div className="pos-section-title">매장별 매출 비교 (상위 8)</div>
          <div className="pos-legend">
            <span><i style={{ background: C_ONETAK }} />onetak 결제매출</span>
            <span><i style={{ background: C_POS }} />POS 전송매출</span>
          </div>
          <div className="pos-chart">
            {loading && <div className="empty-row">불러오는 중...</div>}
            {!loading && error && <div className="empty-row">{error}</div>}
            {!loading && !error && storeChartData.length === 0 && <div className="empty-row">매출 데이터가 없습니다.</div>}
            {!loading && !error && storeChartData.length > 0 && (
              <ResponsiveBar
                data={storeChartData}
                keys={['onetak', 'POS']}
                indexBy="store"
                groupMode="grouped"
                margin={{ top: 12, right: 16, bottom: 64, left: 64 }}
                padding={0.28}
                innerPadding={3}
                colors={[C_ONETAK, C_POS]}
                enableLabel={false}
                axisLeft={{ format: manFmt }}
                axisBottom={{ tickRotation: -22 }}
                valueFormat={(v) => `${Number(v).toLocaleString()}원`}
                theme={{ fontFamily: 'inherit', fontSize: 12, textColor: '#666' }}
                tooltip={({ id, indexValue, value }) => chartTooltip(`${indexValue} · ${id}`, value)}
                animate={false}
              />
            )}
          </div>
        </div>

        <div className="card pos-card">
          <div className="pos-section-title">매장별 매출 상세</div>
          <div className="table-wrap">
            <table className="data-table pos-store-table">
              <thead>
                <tr>
                  <th>매장</th>
                  <th className="pos-num">onetak 매출</th>
                  <th className="pos-num">onetak 건수</th>
                  <th className="pos-num">POS 매출</th>
                  <th className="pos-num">POS 건수</th>
                  <th className="pos-num">차액</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} className="empty-row">불러오는 중...</td></tr>}
                {!loading && error && <tr><td colSpan={6} className="empty-row">{error}</td></tr>}
                {!loading && !error && rows.length === 0 && <tr><td colSpan={6} className="empty-row">매출 데이터가 없습니다.</td></tr>}
                {!loading && !error && rows.map((r) => {
                  const d = (Number(r.onetakAmount) || 0) - (Number(r.posAmount) || 0);
                  return (
                    <tr
                      key={r.storeId}
                      className={selectedStore?.storeId === r.storeId ? 'selected' : ''}
                      onClick={() => setSelectedStore({ storeId: r.storeId, storeName: r.storeName })}
                      title="클릭하면 상품별 매출을 봅니다"
                    >
                      <td style={{ fontWeight: 600 }}>{r.storeName}</td>
                      <td className="pos-num">{won(r.onetakAmount)}</td>
                      <td className="pos-num">{num(r.onetakCount)}</td>
                      <td className="pos-num pos-pos-val">{won(r.posAmount)}</td>
                      <td className="pos-num">{num(r.posCount)}</td>
                      <td className={`pos-num ${d > 0 ? 'pos-neg' : 'pos-ok'}`}>{won(d)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {selectedStore && (
          <div className="card pos-card">
            <div className="pos-detail-head">
              <div>
                <h2>{selectedStore.storeName} · 상품별 매출</h2>
                <div className="subtitle">선택한 기간의 결제완료 주문 기준 상품별 매출입니다.</div>
              </div>
              <button type="button" className="btn btn-secondary" onClick={() => setSelectedStore(null)}>닫기</button>
            </div>

            {productLoading && <div className="empty-row">불러오는 중...</div>}
            {!productLoading && productError && <div className="empty-row">{productError}</div>}
            {!productLoading && !productError && products.length === 0 && <div className="empty-row">이 기간 매출이 없습니다.</div>}

            {!productLoading && !productError && products.length > 0 && (
              <>
                <div className="pos-chart-row">
                  <div className="pos-chart-bar">
                    <ResponsiveBar
                      data={products.slice(0, 10).map((p) => ({ product: p.productName, 매출: Number(p.amount) || 0 })).reverse()}
                      keys={['매출']}
                      indexBy="product"
                      layout="horizontal"
                      margin={{ top: 8, right: 24, bottom: 36, left: 130 }}
                      padding={0.32}
                      colors={[C_ONETAK]}
                      enableGridX
                      enableGridY={false}
                      enableLabel={false}
                      axisBottom={{ format: manFmt }}
                      valueFormat={(v) => `${Number(v).toLocaleString()}원`}
                      theme={{ fontFamily: 'inherit', fontSize: 12, textColor: '#666' }}
                      tooltip={({ indexValue, value }) => chartTooltip(indexValue, value)}
                      animate={false}
                    />
                  </div>
                  <div className="pos-chart-pie">
                    <ResponsivePie
                      data={pieData}
                      margin={{ top: 24, right: 70, bottom: 24, left: 70 }}
                      innerRadius={0.55}
                      padAngle={1}
                      cornerRadius={2}
                      colors={{ scheme: 'reds' }}
                      borderWidth={1}
                      borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                      arcLinkLabel={(d) => clip(d.id)}
                      arcLinkLabelsSkipAngle={10}
                      arcLinkLabelsColor={{ from: 'color' }}
                      arcLinkLabelsTextColor="#666"
                      arcLinkLabelsThickness={1.5}
                      arcLinkLabelsDiagonalLength={12}
                      arcLinkLabelsStraightLength={14}
                      arcLabel={(d) => `${Math.round((d.value / pieTotal) * 100)}%`}
                      arcLabelsSkipAngle={18}
                      arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2.2]] }}
                      valueFormat={(v) => `${Number(v).toLocaleString()}원`}
                      tooltip={({ datum }) => chartTooltip(datum.id, datum.value)}
                      theme={{ fontFamily: 'inherit', fontSize: 11 }}
                      animate={false}
                    />
                  </div>
                </div>

                <div className="table-wrap" style={{ marginTop: 8 }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>상품</th>
                        <th className="pos-num">수량</th>
                        <th className="pos-num">매출</th>
                        <th className="pos-num">주문수</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((p) => (
                        <tr key={p.productId}>
                          <td style={{ fontWeight: 600 }}>{p.productName}</td>
                          <td className="pos-num">{num(p.qty)}</td>
                          <td className="pos-num pos-pos-val">{won(p.amount)}</td>
                          <td className="pos-num">{num(p.orderCount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
